import type { ActivitySummaryParams } from '@doneverse/data'
import {
  ACTIVITY_SUMMARY_EARLIEST_DATE,
  ACTIVITY_SUMMARY_MAX_DAYS,
  ACTIVITY_SUMMARY_PRESETS,
  type ActivitySummaryPreset,
  type ActivitySummaryWindow,
} from '@doneverse/shared'
import type { AdminActivityLogSearch } from './activityLogSearch'
import { isSearchDate } from './activityRange'

/** ClickUp 14zf25zkw26. The Fleet Activity Summary's own URL contract. Dates
 *  are UTC calendar dates (the server's 08:00 PH windows are keyed by them). */
export type ActivitySummaryPresetKey = ActivitySummaryPreset | 'custom'

export interface AdminActivitySummarySearch {
  preset?: ActivitySummaryPresetKey
  from?: string
  to?: string
  q?: string
  flagged?: true
}

export const ACTIVITY_SUMMARY_DEFAULT_PRESET: ActivitySummaryPresetKey = '7d'

const PRESET_KEYS: readonly string[] = [...ACTIVITY_SUMMARY_PRESETS, 'custom']

export function validateActivitySummarySearch(search: Record<string, unknown>): AdminActivitySummarySearch {
  const result: AdminActivitySummarySearch = {}
  if (PRESET_KEYS.includes(search.preset as string)) result.preset = search.preset as ActivitySummaryPresetKey
  if (isSearchDate(search.from)) result.from = search.from
  if (isSearchDate(search.to)) result.to = search.to
  // Like the log's `searchTermFromParam` (a numeric `?q=1234` is a term), but without
  // its API minimum length: this page filters in the browser, so "ma" is a fine term.
  const q = (
    typeof search.q === 'number' ? String(search.q) : typeof search.q === 'string' ? search.q : ''
  ).trim()
  if (q) result.q = q
  if (search.flagged === true || search.flagged === 'true') result.flagged = true
  return result
}

const DAY_MS = 86_400_000
/** Days since the epoch for a validated `yyyy-MM-dd`, read as UTC so the host
 *  timezone cannot shift it. */
const utcDay = (date: string): number => Date.parse(`${date}T00:00:00Z`) / DAY_MS

/** The `yyyy-MM-dd` after a validated one, computed on the UTC calendar so the
 *  host timezone cannot shift it. */
export function nextUtcDate(date: string): string {
  return new Date((utcDay(date) + 1) * DAY_MS).toISOString().slice(0, 10)
}

export function summaryParamsFromSearch(
  s: AdminActivitySummarySearch,
): ActivitySummaryParams | { problem: string } {
  const preset = s.preset ?? ACTIVITY_SUMMARY_DEFAULT_PRESET
  if (preset !== 'custom') return { preset }
  if (!s.from || !s.to) return { problem: 'Pick both a start and an end date.' }
  if (s.to < s.from) return { problem: 'The end date is before the start date.' }
  if (s.from < ACTIVITY_SUMMARY_EARLIEST_DATE) {
    return { problem: `Activity summaries start on ${ACTIVITY_SUMMARY_EARLIEST_DATE}.` }
  }
  if (utcDay(s.to) - utcDay(s.from) + 1 > ACTIVITY_SUMMARY_MAX_DAYS) {
    return { problem: `Pick at most ${ACTIVITY_SUMMARY_MAX_DAYS} days.` }
  }
  return { start: s.from, end: s.to }
}

/** The Activity Log has no 08:00-08:00 window, and its custom range is local
 *  calendar days, so this opens the days covering the window: `from` the first
 *  day, `to` the day after the last (the window ends 08:00 PH on it). A
 *  deliberate superset for browsers at or east of UTC (the log reads from/to as
 *  local calendar days); a 7-day window yields 8 days, past the log's 7-day
 *  trend cap. `preset` is always set; omitted, the log opens on shift. */
export function activityLogLinkForWindow(
  doerId: string,
  window: ActivitySummaryWindow,
): AdminActivityLogSearch {
  const to = nextUtcDate(window.end)
  return { doerId, preset: 'custom', from: window.start, to }
}
